import React, { useEffect, useMemo, useState } from "react";

import { AiUploadDataFileInfo } from "../../../../services/aiChat";
import {
  DEFAULT_INIT_PROMPT,
  extractDataSummary,
} from "../../hooks/useInitContainer";
import { useStreamChat } from "../../hooks/useStreamChat";
import {
  REBOOT_LIVE_REPORT_CONTEXT,
  REBOOT_LIVE_REPORT_INSTRUCTION,
} from "../../RebootLiveInterface";

interface InitStreamState {
  isStreaming: boolean;
  streamedContent: string;
  error: Error | null;
}

interface TestingPanelProps {
  fileIds: string[];
  filesList?: AiUploadDataFileInfo[];
  dataSummary?: string;
  defaultReportPrompt?: string;
  containerId?: string;
  initContainer?: (fileIds: string[], initPrompt?: string) => void;
  initStream?: InitStreamState;
}

export const TestingPanel: React.FC<TestingPanelProps> = ({
  fileIds,
  filesList,
  dataSummary,
  defaultReportPrompt,
  containerId,
  initContainer,
  initStream,
}) => {
  // Init prompt state — editable so the team can tweak the warm-up prompt and
  // re-fire it without leaving the panel. Default mirrors the hook's default.
  const [initPrompt, setInitPrompt] = useState(DEFAULT_INIT_PROMPT);

  // Report Generation state
  const [reportContainerId, setReportContainerId] = useState("");
  const [reportStreamContent, setReportStreamContent] = useState("");
  // Editable copies of the prod constants. Changes are local to this panel
  // and don't affect the production flows that import the constants directly.
  const [customContext, setCustomContext] = useState(
    REBOOT_LIVE_REPORT_CONTEXT,
  );
  const [outputInstruction, setOutputInstruction] = useState(
    REBOOT_LIVE_REPORT_INSTRUCTION,
  );
  // User-editable portion of the report prompt. The DATA SUMMARY block is
  // appended automatically from `dataSummary` so init re-runs refresh it
  // without clobbering edits the user made above it. `null` means "use the
  // form-driven defaultReportPrompt"; any string means the user has edited.
  const [userPromptOverride, setUserPromptOverride] = useState<string | null>(
    null,
  );

  // Seed report container ID from the silent warm-up triggered after Load Data.
  useEffect(() => {
    if (containerId) setReportContainerId(containerId);
  }, [containerId]);

  // The form-driven prompt plus the auto-appended data summary — the editable
  // starting point for the textarea. `userPromptOverride` takes over once the
  // user types, so the entire prompt (summary included) is editable.
  const composedReportPrompt = useMemo(() => {
    const base = defaultReportPrompt ?? "";
    // `dataSummary` is the clean, delimiter-extracted summary from the warm-up.
    // Fall back to extracting from the live stream during a manual re-run before
    // onComplete has populated dataSummary.
    const summaryText =
      dataSummary || extractDataSummary(initStream?.streamedContent || "");
    const summary = summaryText ? `## DATA SUMMARY\n\n${summaryText}` : "";
    return [base, summary].filter(Boolean).join("\n\n");
  }, [defaultReportPrompt, dataSummary, initStream?.streamedContent]);

  // Drop user edits when the composed prompt changes (form edits or a fresh
  // init summary) so the new content takes over; otherwise edits are preserved.
  useEffect(() => {
    setUserPromptOverride(null);
  }, [composedReportPrompt]);

  const reportPrompt = userPromptOverride ?? composedReportPrompt;

  const handleReportPromptChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setUserPromptOverride(e.target.value);
  };

  // Report generation stream
  const reportStream = useStreamChat({
    onContent: (_chunk, fullContent) => setReportStreamContent(fullContent),
  });

  const handleReinitContainer = () => {
    if (!initContainer || fileIds.length === 0) return;
    initContainer(fileIds, initPrompt);
  };

  const handleReportGenerate = () => {
    if (!reportPrompt.trim()) return;
    setReportStreamContent("");

    // dataSummary is now embedded in the prompt textarea (see AnalysisBuilderMode),
    // so custom_context stays minimal to avoid double-sending the same text.
    reportStream.streamChat({
      session_id: `test_report_${Date.now()}`,
      messages: [
        {
          role: "user",
          content: [
            {
              type: "text",
              text: outputInstruction.trim()
                ? `${reportPrompt}\n\n${outputInstruction}`
                : reportPrompt,
            },
          ],
        },
      ],
      container_id: reportContainerId || undefined,
      custom_context: customContext || undefined,
      // Don't persist test-panel report runs. The backend doesn't honor this
      // yet, but keep the frontend ready for when it does (Christina's flow).
      save_chat: false,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch((err) => {
      console.error("Failed to copy to clipboard:", err);
    });
  };

  // Extract the first ```html ... ``` block from the response. The report
  // prompt is designed to produce a single HTML report, so we intentionally
  // only render the first block.
  const extractHtml = (content: string): string | null => {
    const match = content.match(/```html\s*\n?([\s\S]*?)```/);
    return match ? match[1].trim() : null;
  };

  const reportHtml = reportStreamContent
    ? extractHtml(reportStreamContent)
    : null;

  const inputClass =
    "w-full bg-transparent border border-white/20 text-white rounded-md p-2 text-sm focus:outline-none focus:border-[#fb6a07]";
  const buttonClass =
    "bg-[#fb6a07] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#e55e06] disabled:opacity-50 disabled:cursor-not-allowed";
  const cardClass =
    "bg-[#1d1d1f] border border-white/10 rounded-lg p-4 flex flex-col gap-3";
  const labelClass =
    "text-white/60 text-xs font-medium uppercase tracking-wide";
  const copyBtnClass =
    "text-[#fb6a07] text-xs cursor-pointer hover:underline ml-2";

  return (
    <div className="w-full max-w-[1000px] flex flex-col gap-6 mt-6">
      <div className="text-white/40 text-xs uppercase tracking-widest text-center">
        Testing Panel
      </div>

      {/* Section 1: Data Upload Results */}
      <div className={cardClass}>
        <div className={labelClass}>1. Data Upload Results</div>
        {fileIds.length > 0 ? (
          fileIds.map((id) => {
            const fileInfo = filesList?.find((f) => f.id === id);
            return (
              <div key={id} className="flex items-center gap-2">
                <div className="flex-1">
                  {fileInfo && (
                    <div className="text-white/50 text-xs">
                      {fileInfo.filename}
                    </div>
                  )}
                  <code className="text-green-400 text-sm break-all">{id}</code>
                </div>
                <button
                  className={copyBtnClass}
                  onClick={() => copyToClipboard(id)}
                >
                  Copy
                </button>
              </div>
            );
          })
        ) : (
          <div className="text-white/40 text-sm">No files uploaded</div>
        )}
        {dataSummary && (
          <details className="mt-2">
            <summary className="text-white/40 text-xs cursor-pointer hover:text-white/60">
              Data Summary
            </summary>
            <pre className="text-white/60 text-xs mt-2 whitespace-pre-wrap max-h-[200px] overflow-auto">
              {dataSummary}
            </pre>
          </details>
        )}
        {initContainer && fileIds.length > 0 && (
          <details className="mt-2">
            <summary className="text-white/40 text-xs cursor-pointer hover:text-white/60">
              Init Prompt
            </summary>
            <div className="mt-2 flex flex-col gap-2">
              <textarea
                className={`${inputClass} min-h-[80px] resize-y font-mono text-xs leading-relaxed`}
                value={initPrompt}
                onChange={(e) => setInitPrompt(e.target.value)}
                placeholder="Prompt sent to warm up the container..."
              />
              <button
                className={buttonClass}
                onClick={handleReinitContainer}
                disabled={
                  !initPrompt.trim() || initStream?.isStreaming === true
                }
              >
                {initStream?.isStreaming ? "Running Init..." : "Re-run Init"}
              </button>
              <div className="text-white/40 text-xs">
                Re-fires the silent warm-up with this prompt. Updates Container
                ID and Data Summary on completion.
              </div>
              {initStream?.streamedContent && (
                <details open>
                  <summary className="text-white/40 text-xs cursor-pointer hover:text-white/60">
                    Init Response
                  </summary>
                  <pre className="text-white/60 text-xs mt-2 whitespace-pre-wrap max-h-[300px] overflow-auto">
                    {initStream.streamedContent}
                  </pre>
                </details>
              )}
              {initStream?.error && (
                <div className="text-red-400 text-xs">
                  Error: {initStream.error.message}
                </div>
              )}
            </div>
          </details>
        )}
      </div>

      {/* Section 2: Generate Report */}
      <div className={cardClass}>
        <div className={labelClass}>2. Generate Report</div>
        <div>
          <div className="text-white/40 text-xs mb-1">Container ID</div>
          <input
            type="text"
            className={inputClass}
            value={reportContainerId}
            onChange={(e) => setReportContainerId(e.target.value)}
            placeholder="container_..."
          />
          {!reportContainerId && (
            <div className="text-[#fb6a07] text-xs mt-1">
              Wait for the container ID to populate from the post–Load Data
              warm-up before generating, otherwise the report runs without the
              uploaded data.
            </div>
          )}
        </div>
        <div>
          <div className="text-white/40 text-xs mb-1">Report Prompt</div>
          <textarea
            className={`${inputClass} min-h-[360px] resize-y font-mono text-xs leading-relaxed`}
            value={reportPrompt}
            onChange={handleReportPromptChange}
            placeholder="e.g. Generate an HTML report correlating pitch velocity and arm momentum..."
          />
        </div>
        <details>
          <summary className="text-white/40 text-xs cursor-pointer hover:text-white/60">
            Custom Context (sent as custom_context)
          </summary>
          <textarea
            className={`${inputClass} min-h-[140px] resize-y font-mono text-xs leading-relaxed mt-2`}
            value={customContext}
            onChange={(e) => setCustomContext(e.target.value)}
            placeholder="System-level context appended to the model's instructions..."
          />
        </details>
        <details>
          <summary className="text-white/40 text-xs cursor-pointer hover:text-white/60">
            Output Instruction (appended to user message)
          </summary>
          <textarea
            className={`${inputClass} min-h-[140px] resize-y font-mono text-xs leading-relaxed mt-2`}
            value={outputInstruction}
            onChange={(e) => setOutputInstruction(e.target.value)}
            placeholder="Reminder appended to every user message to constrain output format..."
          />
        </details>
        <button
          className={buttonClass}
          onClick={handleReportGenerate}
          disabled={
            reportStream.isStreaming ||
            !reportPrompt.trim() ||
            initStream?.isStreaming === true
          }
        >
          {initStream?.isStreaming
            ? "Initializing..."
            : reportStream.isStreaming
              ? "Generating..."
              : "Generate Report"}
        </button>
        {initStream?.isStreaming && !reportStream.isStreaming && (
          <div className="flex items-center gap-2 text-[#fb6a07] text-xs">
            <span className="size-3 rounded-full border-2 border-[#252527] border-t-orange-500 animate-spin" />
            Waiting for the container warm-up to finish...
          </div>
        )}
        {reportStream.isStreaming && (
          <div className="flex items-center gap-2 text-[#fb6a07] text-xs">
            <span className="inline-block w-2 h-2 rounded-full bg-[#fb6a07] animate-pulse" />
            {reportStreamContent
              ? "Streaming response..."
              : "Waiting for first response chunk..."}
          </div>
        )}
        {reportStreamContent && (
          <>
            <details open={!reportHtml}>
              <summary className="text-white/40 text-xs cursor-pointer hover:text-white/60">
                Raw Response
              </summary>
              <div className="text-white/80 text-sm mt-2 whitespace-pre-wrap max-h-[400px] overflow-auto">
                {reportStreamContent}
              </div>
            </details>
            {reportHtml && (
              <div className="mt-2">
                <div className="text-white/40 text-xs mb-2">
                  Rendered Report
                </div>
                <iframe
                  srcDoc={reportHtml}
                  title="Generated Report"
                  sandbox="allow-scripts"
                  referrerPolicy="no-referrer"
                  className="w-full bg-white rounded-md"
                  style={{ minHeight: "600px", border: "none" }}
                />
              </div>
            )}
          </>
        )}
        {reportStream.error && (
          <div className="text-red-400 text-xs">
            Error: {reportStream.error.message}
          </div>
        )}
      </div>
    </div>
  );
};
